// ============================================================================
// ConversationList — sidebar of saved AM Co-pilot chats. Added 2026-05-21.
//
// Reuses no external primitive (the app doesn't ship shadcn/ui or Radix —
// see HANDOVER 2026-05-21). Renders as a fixed-width left rail on md+
// screens and as an overlay sheet on mobile (rendered by the parent, see
// copilot-page.tsx). Lives alongside chat-input / chat-message rather than
// in a `components/` subfolder to match existing module conventions.
//
// Per-row actions (archive, rename) live behind a small inline kebab — open
// state is local to the row so taps outside dismiss it.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { Archive, MoreVertical, Plus, RotateCcw } from 'lucide-react';
import type { CopilotConversation } from '../../shared/types';

interface ConversationListProps {
  conversations: CopilotConversation[];
  activeId: string | null;
  isLoading?: boolean;
  /** True when a previous chat exists to resume (CW-G Phase 1, AFB-0066). */
  canResumeLast?: boolean;
  showArchived?: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onResumeLast?: () => void;
  onToggleArchived?: (next: boolean) => void;
  onArchive: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
}

export default function ConversationList({
  conversations,
  activeId,
  isLoading = false,
  canResumeLast = false,
  showArchived = false,
  onSelect,
  onNew,
  onResumeLast,
  onToggleArchived,
  onArchive,
  onUnarchive,
  onRename,
}: ConversationListProps) {
  return (
    <aside className="flex h-full w-full flex-col gap-2 border-r border-tcs-border bg-tcs-raised p-2">
      <button
        type="button"
        onClick={onNew}
        className="flex min-h-[44px] w-full items-center justify-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-text-secondary hover:border-tcs-border-input hover:text-tcs-text"
      >
        <Plus className="h-4 w-4" />
        <span>New chat</span>
      </button>

      {/* 2026-08-07 (CW-G Phase 1, AFB-0066): /copilot opens a NEW thread by
          default and no longer auto-resumes the last one. This is the explicit
          affordance that replaces that behaviour — history stays one click
          away, it just isn't the default any more. */}
      {canResumeLast && !showArchived && onResumeLast && (
        <button
          type="button"
          onClick={onResumeLast}
          className="flex min-h-[44px] w-full items-center justify-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-tcs-text-secondary hover:bg-tcs-bg-muted hover:text-tcs-text"
        >
          <RotateCcw className="h-4 w-4" />
          <span>Resume last chat</span>
        </button>
      )}

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="px-2 py-4 text-xs text-tcs-text-muted">Loading…</div>
        ) : conversations.length === 0 ? (
          <div className="px-2 py-4 text-xs text-tcs-text-muted">
            {showArchived ? 'No archived chats.' : 'No saved chats yet.'}
          </div>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {conversations.map((c) => (
              <ConversationRow
                key={c.id}
                conv={c}
                isActive={c.id === activeId}
                isArchivedView={showArchived}
                onSelect={() => onSelect(c.id)}
                onArchive={() => onArchive(c.id)}
                onUnarchive={onUnarchive ? () => onUnarchive(c.id) : undefined}
                onRename={(newTitle) => onRename(c.id, newTitle)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Archived chats are hidden, never deleted. This is the filter that
          gets them back — without it, Archive reads as a destructive action. */}
      {onToggleArchived && (
        <button
          type="button"
          onClick={() => onToggleArchived(!showArchived)}
          className="flex min-h-[44px] w-full items-center justify-center gap-1 rounded-md border-t border-tcs-surface px-3 py-2 text-xs font-medium text-tcs-text-muted hover:bg-tcs-bg-muted hover:text-tcs-text"
        >
          <Archive className="h-3.5 w-3.5" />
          <span>{showArchived ? 'Back to active chats' : 'Show archived'}</span>
        </button>
      )}
    </aside>
  );
}

interface ConversationRowProps {
  conv: CopilotConversation;
  isActive: boolean;
  isArchivedView?: boolean;
  onSelect: () => void;
  onArchive: () => void;
  onUnarchive?: () => void;
  onRename: (newTitle: string) => void;
}

function ConversationRow({
  conv,
  isActive,
  isArchivedView = false,
  onSelect,
  onArchive,
  onUnarchive,
  onRename,
}: ConversationRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);

  // Dismiss the inline menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleRenameClick = () => {
    setMenuOpen(false);
    const next = window.prompt('Rename chat', conv.title ?? '');
    if (next != null && next.trim().length > 0) {
      onRename(next.trim());
    }
  };

  const handleArchiveClick = () => {
    setMenuOpen(false);
    if (
      window.confirm(
        `Archive "${conv.title ?? 'Untitled'}"? It will be hidden from this list — you can get it back with "Show archived".`,
      )
    ) {
      onArchive();
    }
  };

  const handleUnarchiveClick = () => {
    setMenuOpen(false);
    onUnarchive?.();
  };

  return (
    <li
      ref={rowRef}
      className={`group relative flex min-h-[44px] items-center justify-between gap-1 rounded-md px-2 py-1 text-sm ${
        isActive ? 'bg-tcs-surface' : 'hover:bg-tcs-bg-muted'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="min-h-[44px] flex-1 truncate text-left text-tcs-text-secondary"
        title={conv.title ?? 'Untitled'}
      >
        <div className="truncate font-medium">{conv.title ?? 'Untitled'}</div>
        <div className="truncate text-xs text-tcs-text-muted">
          {formatRelative(conv.updated_at)}
        </div>
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text-secondary"
        aria-label="More actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {menuOpen && (
        <div
          className="absolute right-2 top-11 z-20 w-36 rounded-md border border-tcs-border bg-tcs-overlay py-1 text-sm shadow"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={handleRenameClick}
            className="block min-h-[44px] w-full px-3 py-2 text-left text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Rename
          </button>
          {isArchivedView ? (
            <button
              type="button"
              onClick={handleUnarchiveClick}
              className="block min-h-[44px] w-full px-3 py-2 text-left text-tcs-text-secondary hover:bg-tcs-bg-muted"
            >
              Unarchive
            </button>
          ) : (
            <button
              type="button"
              onClick={handleArchiveClick}
              className="block min-h-[44px] w-full px-3 py-2 text-left text-tcs-text-secondary hover:bg-tcs-bg-muted"
            >
              Archive
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Relative-time helper — keeps the rail readable without pulling date-fns.
// ────────────────────────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (diff < min) return 'just now';
  if (diff < hour) return `${Math.floor(diff / min)} min ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 2 * day) return 'Yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
